import type { IAuditLog } from '../../../src/engine/AuditLog';
import type { IGraphClient } from '../../../src/services/GraphService';
import { buildChecklist, type Answers, type IChecklistLine, type IPerson, type IProgram } from '../../api/src/programs';
import type { IWorkspace } from '../../api/src/workspace';
import { getItemByPath } from '../graphx';
import type { IRecord } from '../lists/ListStore';
import type { RecordBatch } from '../lists/RecordWriter';
import type { FirmRecords, RecordSet } from './FirmRecords';
import { ensureFolders, folderName } from './folders';
import { ageOn, type IClientView, localToday, outstandingCount, stageLabel } from './model';
import { CLIENTS_FOLDER } from './paths';

export interface INewPerson {
  role: string;
  name: string;
  birthDate?: string;
  relationship?: string;
}

export interface INewApplication {
  client: IClientView;
  program: IProgram;
  answers: Answers;
  people: INewPerson[];
  lawyer?: string;
  clerk?: string;
  retainerSigned: string;
  representativeSigned: string;
  /** Kept in the restricted lists and library (refugee and private-sponsorship files by default). */
  restricted: boolean;
}

export interface IOpenContext {
  graph: IGraphClient;
  manageGraph: IGraphClient;
  audit: IAuditLog;
  workspace: IWorkspace;
  records: FirmRecords;
  user: string;
  /** Counts the new file (priced per file; the count only, not the program); refused when the free trial's files are used. */
  countOpened: () => Promise<unknown>;
}

/** The client's folder: "Khalil, Omar (2026-014)". */
export function clientFolder(client: { name: string; number?: string }): string {
  return `${CLIENTS_FOLDER}/${folderName(client.number ? `${client.name} (${client.number})` : client.name)}`;
}

/** The application's folder, its person folders, and the shared ones. */
export function applicationFolders(client: { name: string; number?: string }, program: IProgram, opened: string, people: { name: string }[]): { root: string; all: string[] } {
  const root: string = `${clientFolder(client)}/${folderName(`${program.short} ${opened}`)}`;
  const seen: { [n: string]: boolean } = {};
  const persons: string[] = [];
  for (const p of people) {
    const n: string = folderName(p.name);
    if (!seen[n.toLowerCase()]) {
      seen[n.toLowerCase()] = true;
      persons.push(`${root}/${n}`);
    }
  }
  return { root, all: [root, ...persons, `${root}/IRCC correspondence`, `${root}/From client`] };
}

/** What gates opening a file, in plain words; empty when it can be opened. */
export function gateProblems(a: Pick<INewApplication, 'client' | 'retainerSigned' | 'representativeSigned'>): string[] {
  const out: string[] = [];
  if (!a.client.conflictOn) {
    out.push('Run and record the conflict check for this client first.');
  }
  if (!a.client.idOn) {
    out.push('Record how the client\'s identity was verified first.');
  }
  if (!a.retainerSigned) {
    out.push('Enter the date the retainer was signed.');
  }
  if (!a.representativeSigned) {
    out.push('Enter the date the Use of a Representative form (IMM 5476) was signed.');
  }
  return out;
}

/**
 * Opens an application: counts it (priced per file), creates its folders (logged, undoable),
 * then its record, its people and its checklist in the firm's lists, all as one batch in History.
 */
export async function openApplication(ctx: IOpenContext, a: INewApplication, onProgress: (text: string) => void): Promise<IRecord> {
  const problems: string[] = gateProblems(a);
  if (problems.length) {
    throw new Error(problems.join(' '));
  }
  const opened: string = localToday();
  onProgress('Counting the new file…');
  await ctx.countOpened();

  const set: RecordSet | undefined = a.restricted ? ctx.records.restricted : ctx.records.main;
  if (!set) {
    throw new Error('Restricted files aren\'t set up yet. A site owner can set them up in Settings.');
  }
  onProgress('Creating its folders…');
  const folders = applicationFolders(a.client, a.program, opened, a.people);
  const library: IWorkspace = { ...ctx.workspace, driveId: set.library.driveId, libraryName: set.library.libraryName };
  await ensureFolders(ctx.manageGraph, library, set.audit, ctx.user, folders.all);
  const folder = await getItemByPath(ctx.graph, set.library.driveId, folders.root);

  const records = set;
  const batch: RecordBatch = set.writer.batch();
  const title: string = `${a.program.short}: ${a.client.name}`;
  onProgress('Saving the application…');
  const application: IRecord = await batch.create(records.applications, {
    Title: title, ClientKey: a.client.key, Program: a.program.id, ProgramVersion: a.program.version, Answers: JSON.stringify(a.answers),
    Stage: stageLabel('collecting'), Open: true, Lawyer: a.lawyer, Clerk: a.clerk, Opened: opened,
    RetainerSigned: a.retainerSigned, RepresentativeSigned: a.representativeSigned, Restricted: a.restricted,
    FolderId: folder?.id, FolderUrl: folder?.webUrl
  }, title, `Opened ${title}`);

  const people: IPerson[] = [];
  for (const p of a.people) {
    onProgress(`Adding ${p.name}…`);
    const r: IRecord = await batch.create(records.people, {
      Title: p.name, ApplicationKey: application.key, ClientKey: a.client.key, Role: p.role, BirthDate: p.birthDate, Relationship: p.relationship
    }, p.name, `Added ${p.name} to ${title}`);
    people.push({ key: r.key, role: p.role, name: p.name, age: ageOn(p.birthDate, opened) });
  }

  const lines: IChecklistLine[] = buildChecklist(a.program, a.answers, people);
  let n: number = 0;
  for (const l of lines) {
    onProgress(`Building the checklist (${++n} of ${lines.length})…`);
    await batch.create(records.checklist, {
      Title: l.label, ApplicationKey: application.key, PersonKey: l.personKey, ItemId: l.itemId, Section: l.section, Form: l.form,
      Status: 'Needed', ChangedBy: ctx.user, ChangedOn: new Date().toISOString()
    }, l.label, `Added "${l.label}" to the checklist`);
  }
  await records.applications.update(application.id, { Outstanding: outstandingCount(lines.map(() => ({ status: 'Needed' }))) });
  return application;
}
